import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import moment from "moment";
import toast from "react-hot-toast";

function ModalRetiradaAtendimento({
  open,
  handleClose,
  sendRequest,
  auth,
  clinicaId,
  stock,
  atualizarStock,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [data, setData] = useState(moment().format("YYYY-MM-DD"));
  const [atendimentos, setAtendimentos] = useState([]);
  const [loadingAtendimentos, setLoadingAtendimentos] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchClientes = async () => {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/nomeid/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setClientes(response.clientes || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchClientes();
  }, [open]);

  const handleClose2 = () => {
    setClienteSelecionado(null);
    setAtendimentos([]);
    setData(moment().format("YYYY-MM-DD"));
    setActiveTab(0);
    handleClose();
  };

  // Buscar lotes disponíveis de um produto no stock
  const getLotesDisponiveis = (produtoId) => {
    if (!stock || !stock.stock) return [];
    const produtoStock = stock.stock.find(
      (s) =>
        s.produto._id === produtoId ||
        s.produto._id?.toString() === produtoId?.toString(),
    );
    if (!produtoStock) return [];
    return produtoStock.lotes
      .filter((l) => !l.esgotado && l.quantidade > 0)
      .sort((a, b) => new Date(a.dataImportacao) - new Date(b.dataImportacao));
  };

  // Buscar todos os produtos com stock disponível
  const getProdutosComStock = () => {
    if (!stock || !stock.stock) return [];
    return stock.stock
      .filter((s) => s.lotes.some((l) => !l.esgotado && l.quantidade > 0))
      .map((s) => s.produto);
  };

  // Lote mais antigo de um produto
  const getLoteMaisAntigo = (produtoId) => {
    const lotes = getLotesDisponiveis(produtoId);
    return lotes.length > 0 ? lotes[0] : null;
  };

  // Quantidade disponível de um lote
  const getQuantidadeLote = (produtoId, loteNome) => {
    const lotes = getLotesDisponiveis(produtoId);
    const lote = lotes.find((l) => l.lote === loteNome);
    return lote ? lote.quantidade : 0;
  };

  // Construir linhas de saída a partir do consumoEstimado
  const buildLinhasSaida = (consumoEstimado) => {
    return consumoEstimado
      .map((grupo) => {
        if (!grupo.opcoes || grupo.opcoes.length === 0) return null;

        // Percorrer opções por ordem até encontrar uma com stock
        let opcaoEscolhida = null;
        let lotesDisponiveis = [];

        for (const opcao of grupo.opcoes) {
          const produtoId = opcao.produto?._id || opcao.produto;
          if (!produtoId) continue;

          const lotes = getLotesDisponiveis(produtoId);
          if (lotes.length > 0) {
            opcaoEscolhida = opcao;
            lotesDisponiveis = lotes;
            break;
          }
        }

        // Nenhuma opção tem stock — usar a primeira opção mesmo assim e avisar
        if (!opcaoEscolhida) {
          const primeiraOpcao = grupo.opcoes[0];
          const produtoId =
            primeiraOpcao?.produto?._id || primeiraOpcao?.produto;
          return {
            produtoId: produtoId || "",
            produtoNome: primeiraOpcao?.produto?.nome || "",
            unidadeMedida: primeiraOpcao?.produto?.unidadeMedida || "",
            quantidade: primeiraOpcao?.quantidade || 0,
            lote: "",
            quantidadeDisponivel: 0,
            manual: false,
            localizacaoId: null,
            semStockEmNenhumaOpcao: true,
          };
        }

        const produtoId = opcaoEscolhida.produto?._id || opcaoEscolhida.produto;
        const loteMaisAntigo = lotesDisponiveis[0];
        const foiSubstituido = opcaoEscolhida !== grupo.opcoes[0];

        return {
          produtoId,
          produtoNome: opcaoEscolhida.produto?.nome || "",
          unidadeMedida: opcaoEscolhida.produto?.unidadeMedida || "",
          quantidade: opcaoEscolhida.quantidade,
          lote: loteMaisAntigo.lote,
          localizacaoId:
            loteMaisAntigo.localizacoes?.[0]?.localizacao?._id || null,
          quantidadeDisponivel: loteMaisAntigo.quantidade,
          manual: false,
          foiSubstituido,
          produtoOriginalNome: foiSubstituido
            ? grupo.opcoes[0]?.produto?.nome || ""
            : null,
        };
      })
      .filter(Boolean);
  };

  const buscarAtendimentos = async () => {
    setLoadingAtendimentos(true);
    try {
      const clienteId = activeTab === 0 ? clienteSelecionado?._id : null;
      const url = clienteId
        ? `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/retiradaporatendimento?clinica=${clinicaId}&data=${data}&cliente=${clienteId}`
        : `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/retiradaporatendimento?clinica=${clinicaId}&data=${data}`;

      const response = await sendRequest(url, "GET", null, {
        Authorization: "Bearer " + auth.token,
      });

      // Adicionar linhasSaida a cada tratamento
      const atendimentosComLinhas = (response.atendimentos || []).map(
        (cliente) => ({
          ...cliente,
          tratamentos: cliente.tratamentos.map((trat) => ({
            ...trat,
            linhasSaida: buildLinhasSaida(trat.consumoEstimado),
          })),
        }),
      );

      setAtendimentos(atendimentosComLinhas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAtendimentos(false);
    }
  };

  const atualizarLinha = (
    clienteIndex,
    tratIndex,
    linhaIndex,
    campo,
    valor,
  ) => {
    const novos = [...atendimentos];
    const linha =
      novos[clienteIndex].tratamentos[tratIndex].linhasSaida[linhaIndex];

    if (campo === "lote") {
      const lotes = getLotesDisponiveis(linha.produtoId);
      const loteSeleccionado = lotes.find((l) => l.lote === valor);
      linha.lote = valor;
      linha.quantidadeDisponivel = loteSeleccionado?.quantidade || 0;
      linha.localizacaoId =
        loteSeleccionado?.localizacoes?.[0]?.localizacao?._id || null;
    } else if (campo === "produtoId") {
      const loteMaisAntigo = getLoteMaisAntigo(valor);
      const produto = getProdutosComStock().find((p) => p._id === valor);
      linha.produtoId = valor;
      linha.produtoNome = produto?.nome || "";
      linha.unidadeMedida = produto?.unidadeMedida || "";
      linha.lote = loteMaisAntigo?.lote || "";
      linha.quantidadeDisponivel = loteMaisAntigo?.quantidade || 0;
      linha.localizacaoId =
        loteMaisAntigo?.localizacoes?.[0]?.localizacao?._id || null;
    } else {
      linha[campo] = valor;
    }

    setAtendimentos(novos);
  };

  const adicionarLinha = (clienteIndex, tratIndex) => {
    const novos = [...atendimentos];
    novos[clienteIndex].tratamentos[tratIndex].linhasSaida.push({
      produtoId: "",
      produtoNome: "",
      unidadeMedida: "",
      quantidade: 1,
      lote: "",
      localizacaoId: null,
      quantidadeDisponivel: 0,
      manual: true,
    });
    setAtendimentos(novos);
  };

  const removerLinha = (clienteIndex, tratIndex, linhaIndex) => {
    const novos = [...atendimentos];
    novos[clienteIndex].tratamentos[tratIndex].linhasSaida.splice(
      linhaIndex,
      1,
    );
    setAtendimentos(novos);
  };

  const produtosComStock = getProdutosComStock();

  const confirmarRetiradas = async () => {
    const retiradas = [];

    for (const clienteAtendimento of atendimentos) {
      for (const trat of clienteAtendimento.tratamentos) {
        const itensValidos = trat.linhasSaida.filter(
          (l) => l.produtoId && l.lote && l.quantidade > 0,
        );
        if (itensValidos.length === 0) continue;

        retiradas.push({
          clienteId: clienteAtendimento.clienteId,
          tipo: trat.tipo,
          tratamentoIsoladoId: trat.tratamentoIsoladoId,
          planoTratamentoId: trat.planoTratamentoId,
          procedimentoId: trat.procedimentoId,
          sessaoId: trat.sessaoId,
          tratamentoId: trat.tratamentoId,
          designacao: trat.designacao,
          dente: trat.dente,
          faces: trat.faces || [],
          itens: itensValidos.map((l) => ({
            produtoId: l.produtoId,
            lote: l.lote,
            localizacaoId: l.localizacaoId || null,
            quantidade: Number(l.quantidade),
            foiSubstituido: l.foiSubstituido || false,
            produtoOriginalId: l.produtoOriginalId || null,
          })),
        });
      }
    }

    if (retiradas.length === 0) {
      toast.error("Nenhum item válido para confirmar");
      return;
    }

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/confirmar-retirada-atendimento/${clinicaId}`,
        "POST",
        JSON.stringify({ retiradas }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      atualizarStock(response.stock, response.message);
      handleClose2();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose2} maxWidth="lg" fullWidth>
      <DialogTitle>Retirada por Atendimento</DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, val) => {
            setActiveTab(val);
            setAtendimentos([]);
            setClienteSelecionado(null);
          }}
          style={{ marginBottom: "20px" }}
        >
          <Tab icon={<PersonIcon />} label="Por Cliente" iconPosition="start" />
          <Tab
            icon={<CalendarTodayIcon />}
            label="Em Lote (Dia)"
            iconPosition="start"
          />
        </Tabs>

        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "20px",
            alignItems: "center",
          }}
        >
          {activeTab === 0 && (
            <Autocomplete
              options={clientes}
              getOptionLabel={(option) => option.nome}
              value={clienteSelecionado}
              onChange={(_, val) => {
                setClienteSelecionado(val);
                setAtendimentos([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecionar Cliente"
                  size="small"
                />
              )}
              style={{ flex: 2 }}
            />
          )}
          <TextField
            label="Data"
            type="date"
            value={data}
            onChange={(e) => {
              setData(e.target.value);
              setAtendimentos([]);
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: moment().format("YYYY-MM-DD") }}
            style={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={buscarAtendimentos}
            disabled={activeTab === 0 && !clienteSelecionado}
          >
            Buscar
          </Button>
        </div>

        {loadingAtendimentos && <p>A carregar atendimentos...</p>}

        {!loadingAtendimentos && atendimentos.length === 0 && (
          <p style={{ color: "#666", fontStyle: "italic" }}>
            {activeTab === 0 && !clienteSelecionado
              ? "Selecione um cliente e clique em Buscar."
              : "Nenhum atendimento encontrado para os critérios selecionados."}
          </p>
        )}

        {!loadingAtendimentos && atendimentos.length > 0 && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {atendimentos.map((clienteAtendimento, clienteIndex) => (
              <div
                key={clienteIndex}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "16px",
                }}
              >
                <h4 style={{ margin: "0 0 12px 0", color: "#1976d2" }}>
                  {clienteAtendimento.clienteNome}
                </h4>

                {clienteAtendimento.tratamentos.map((trat, tratIndex) => (
                  <div
                    key={tratIndex}
                    style={{
                      marginBottom: "16px",
                      paddingBottom: "16px",
                      borderBottom:
                        tratIndex < clienteAtendimento.tratamentos.length - 1
                          ? "1px dashed #e0e0e0"
                          : "none",
                    }}
                  >
                    {/* Cabeçalho do tratamento */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{trat.designacao}</span>
                      {trat.dente && (
                        <span
                          style={{
                            fontSize: "12px",
                            background: "#e3f2fd",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            color: "#1976d2",
                          }}
                        >
                          Dente {trat.dente}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: "11px",
                          background:
                            trat.tipo === "isolado" ? "#f3e5f5" : "#e8f5e9",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          color:
                            trat.tipo === "isolado" ? "#7b1fa2" : "#2e7d32",
                        }}
                      >
                        {trat.tipo === "isolado" ? "Isolado" : "Plano"}
                      </span>
                    </div>

                    {/* Linhas de saída */}
                    {trat.linhasSaida.length === 0 ? (
                      <p
                        style={{
                          color: "#999",
                          fontSize: "13px",
                          margin: "0 0 8px 0",
                        }}
                      >
                        Sem estimativa de consumo definida.
                      </p>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        {/* Header */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 2fr 1fr auto",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              fontWeight: 500,
                            }}
                          >
                            Produto
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              fontWeight: 500,
                            }}
                          >
                            Lote
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              fontWeight: 500,
                            }}
                          >
                            Quantidade
                          </span>
                          <span></span>
                        </div>

                        {trat.linhasSaida.map((linha, linhaIndex) => {
                          const excedeLote =
                            linha.quantidade > linha.quantidadeDisponivel &&
                            linha.quantidadeDisponivel > 0;
                          const semStock =
                            linha.lote && linha.quantidadeDisponivel === 0;

                          return (
                            <div
                              key={linhaIndex}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "2fr 2fr 1fr auto",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                {/* Produto */}
                                <Autocomplete
                                  options={produtosComStock}
                                  getOptionLabel={(opt) => opt.nome || ""}
                                  value={
                                    produtosComStock.find(
                                      (p) => p._id === linha.produtoId,
                                    ) || null
                                  }
                                  onChange={(_, val) =>
                                    atualizarLinha(
                                      clienteIndex,
                                      tratIndex,
                                      linhaIndex,
                                      "produtoId",
                                      val?._id || "",
                                    )
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder="Produto"
                                    />
                                  )}
                                  disableClearable={!linha.manual}
                                  readOnly={!linha.manual}
                                />

                                {/* Lote */}
                                <TextField
                                  select
                                  size="small"
                                  value={linha.lote}
                                  onChange={(e) =>
                                    atualizarLinha(
                                      clienteIndex,
                                      tratIndex,
                                      linhaIndex,
                                      "lote",
                                      e.target.value,
                                    )
                                  }
                                  SelectProps={{ native: true }}
                                  disabled={!linha.produtoId}
                                >
                                  <option value="">Selecionar lote</option>
                                  {getLotesDisponiveis(linha.produtoId).map(
                                    (l) => (
                                      <option key={l._id} value={l.lote}>
                                        {l.lote} (disp: {l.quantidade})
                                      </option>
                                    ),
                                  )}
                                </TextField>

                                {/* Quantidade + aviso */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={linha.quantidade}
                                    onChange={(e) =>
                                      atualizarLinha(
                                        clienteIndex,
                                        tratIndex,
                                        linhaIndex,
                                        "quantidade",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{ min: 0, step: 0.1 }}
                                  />
                                  {(excedeLote || semStock) && (
                                    <Tooltip
                                      title={
                                        semStock
                                          ? "Lote sem stock disponível"
                                          : `Quantidade excede o disponível (${linha.quantidadeDisponivel})`
                                      }
                                    >
                                      <WarningAmberIcon
                                        style={{
                                          color: "orange",
                                          fontSize: "20px",
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </div>

                                {/* Remover */}
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    removerLinha(
                                      clienteIndex,
                                      tratIndex,
                                      linhaIndex,
                                    )
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>

                                {/* Avisos */}
                                {linha.foiSubstituido && (
                                  <div
                                    style={{
                                      gridColumn: "1/-1",
                                      fontSize: "12px",
                                      color: "#e65100",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <WarningAmberIcon
                                      style={{ fontSize: "14px" }}
                                    />
                                    Produto original sem stock (
                                    {linha.produtoOriginalNome}). A usar
                                    substituto.
                                  </div>
                                )}
                                {linha.semStockEmNenhumaOpcao && (
                                  <div
                                    style={{
                                      gridColumn: "1/-1",
                                      fontSize: "12px",
                                      color: "#d32f2f",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <WarningAmberIcon
                                      style={{ fontSize: "14px" }}
                                    />
                                    Nenhum produto disponível no stock para este
                                    grupo. Adicione manualmente ou ignore.
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Botão adicionar linha */}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => adicionarLinha(clienteIndex, tratIndex)}
                      style={{ fontSize: "12px" }}
                    >
                      Adicionar linha
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose2} color="secondary">
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={atendimentos.length === 0}
          onClick={confirmarRetiradas}
        >
          Confirmar Retiradas
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalRetiradaAtendimento;
